/**
 * LE COMPOSEUR DU STUDIO — la grille d'un niveau, son rail, ses gestes, et l'inspecteur de la cellule choisie.
 *
 * Transposé de la maquette (`isEdit`) : dossier, article, case vide, les trois SOURCES (famille, sous-famille, marque)
 * qui déversent le catalogue, et les trois PRÉRÉGLAGES structurels qui déploient tout un référentiel. Chaque geste rend
 * un nouveau document au conteneur (`onLayout`), qui l'empile pour l'annulation et l'enregistre au brouillon.
 *
 * ⚠️ CE QUI N'EXISTE PAS ENCORE SE DIT, IL NE S'OFFRE PAS : les préréglages DYNAMIQUES (meilleures ventes, nouveautés,
 *    promotions) paraissent au sous-menu, grisés et annoncés « à venir » — ils demandent un ordonnanceur qui n'existe
 *    pas. Proposer un geste sans effet ferait composer ce que la caisse ne montrera pas (§23.0).
 * ⚠️ DANS LE NIVEAU D'UNE SOURCE, une cellule est une tuile POSÉE ou un article DÉVERSÉ (`@<article>`) : retirer un
 *    article déversé le MASQUE — il reste au catalogue, « rétablir » le rend —, le retoucher en fait une tuile à sa place.
 * ⚠️ `layout` EST LA DISPOSITION DÉPLOYÉE (`expandLayout`) : les préréglages y portent leurs tuiles sources, que le
 *    conteneur DÉPOUILLE de ce qui revient par `onLayout`. Une tuile déployée ne se retouche donc pas ici — elle se
 *    retire (sa valeur est masquée) ou se PERSONNALISE (une vraie tuile prend sa place) ; aucun geste ne la vise sinon.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { confirm, Icon, Trans, useTileDrag, type IconName } from '@orgazone/ui-web';
import { QUICK_RAIL, QUICK_TILE, QUICK_VEIL, quickTileVariant, webPaperVeil } from '@orgazone/ui-tokens';
import {
  PRESET_SOURCE, QUICK_POUR_MAX, QUICK_PRESET_KINDS, QUICK_TILE_COLORS, isPresetChildId, isPresetKind, isSourceKind, opensLevel, screenLook,
  type QuickPhotoSize, type QuickPresetKind, type QuickSourceKind, type QuickTileFace, type QuickTileLook,
  type QuickTileVisual,
} from '@orgazone/sync-contract';
import type { StudioArticle, StudioLayout, StudioSource, StudioTile, StudioTileKind } from '../api';
import { t } from '../i18n';
import {
  MAX_TILES, addArticles, addTile, descendantIds, effectivePath, findTiles, folderTargets, hidePoured,
  hiddenCount, isPouredKey, kindLabel, levelCells, materialize, moveTiles, nudgeInSource, nudgeTile, patchTile, pathTo,
  pouredArticle, railButtons, removeTiles, reorderLevel, reorderSource, restoreHidden, setSource, sourceRefOf,
  sourceWord, tileById, tileName, NO_LOOK, NO_POUR, customizePresetValue, hidePresetValues, presetChildOf, presetHiddenCount, presetLabel,
  restorePresetValues, deepTileTotal, sourceTileTotal, type FindHit, type PourState, type PresetValuesState, type TilePatch,
} from './quick-studio-model';
import { RailButton, TILE_COLOR_NAME, TileLookEditor, railStyle, shownPhoto, studioFace, tileTone } from './quick-studio-look';
import {
  STUDIO_CONTROL, StudioTileBody, articleContent, studioGrid, studioTileControls, studioTileFrame, useGridWidth, type StudioTileContent,
  type StudioTileControls,
} from './quick-studio-tile';
import { QuickStudioSourceChooser, QuickStudioSourceList } from './quick-studio-sources';

const MONO = 'var(--font-mono)';
/** Durée du surlignage d'une cellule retrouvée par la recherche. */
const HIT_MS = 1800;

/** Ce que le menu d'ajout propose — une nature de tuile, ou le préréglage annoncé « à venir ». */
type AddKind = StudioTileKind | 'preset';

/**
 * Les natures qu'on ajoute d'ici, dans l'ordre et les teintes de la maquette (`kindsList`).
 * ⚠️ L'AIDE D'UNE SOURCE NE NOMME PAS SON CHAMP, là où la maquette écrit « Déverse les articles d'une famille » : la phrase
 *    traduite graverait un mot que le magasin peut renommer (« Millésime ») — le nom de la nature, lui, passe par son
 *    vocabulaire. Elle tient sur une ligne, comme celle de la maquette.
 */
const ADD_KINDS: { kind: AddKind; icon: IconName; tone: { bg: string; fg: string }; hint: () => string }[] = [
  { kind: 'empty', icon: 'grid', tone: { bg: 'var(--ink-100)', fg: 'var(--ink-500)' }, hint: () => t('Respire, aligne, sépare') },
  { kind: 'folder', icon: 'grid', tone: { bg: 'var(--teal-100)', fg: 'var(--teal-800)' }, hint: () => t('Contient d’autres tuiles, s’ouvre au toucher') },
  { kind: 'family', icon: 'tag', tone: { bg: 'var(--amber-bg)', fg: 'var(--amber-fg)' }, hint: () => t('Déverse ses articles en direct') },
  { kind: 'sub_family', icon: 'tag', tone: { bg: 'var(--amber-bg)', fg: 'var(--amber-fg)' }, hint: () => t('Déverse ses articles en direct') },
  { kind: 'brand', icon: 'sparkles', tone: { bg: 'var(--violet-bg)', fg: 'var(--violet-fg)' }, hint: () => t('Déverse ses articles en direct') },
  { kind: 'article', icon: 'cart', tone: { bg: 'var(--blue-bg)', fg: 'var(--blue-fg)' }, hint: () => t('Un produit, touche unique') },
  { kind: 'preset', icon: 'sparkles', tone: { bg: 'var(--rose-bg)', fg: 'var(--rose-fg)' }, hint: () => t('Listes prêtes : top ventes, promos, toutes les familles…') },
];
const SIZES: { w: 1 | 2; h: 1 | 2 }[] = [{ w: 1, h: 1 }, { w: 2, h: 1 }, { w: 1, h: 2 }, { w: 2, h: 2 }];

/**
 * LES PRÉRÉGLAGES DYNAMIQUES DE LA MAQUETTE — annoncés, pas offerts : ils demandent un calcul périodique au serveur, et
 * aucun ordonnanceur n'existe (cadrage A4). Les proposer composerait des tuiles que la caisse ne saurait pas remplir.
 */
const DYNAMIC_PRESETS: (() => string)[] = [
  () => t('Meilleures ventes — 7 jours'), () => t('Nouveautés du mois'), () => t('Articles en promotion'),
];

export interface ComposerProps {
  /**
   * Ce qui se pose au-dessus du canevas, dans SA colonne — la barre « Disposition » de l'écran. L'inspecteur commence à sa
   * hauteur, dans la colonne de droite (maquette).
   */
  context?: ReactNode;
  layout: StudioLayout;
  articles: Record<string, StudioArticle>;
  sources: Record<string, StudioSource>;
  pours: Record<string, PourState>;
  /** Demande le contenu de ces sources (`sourceRef`) : le conteneur le lit une fois et le garde. */
  onNeedPours: (refs: string[]) => void;
  /** Relit le contenu d'une source dont la lecture a échoué. */
  onRetryPour: (ref: string) => void;
  /** Fait connaître à l'écran une source choisie — son nom, pour la tuile qui la déverse. */
  onSource: (source: StudioSource) => void;
  /** Les valeurs que les préréglages déploient, par référentiel — leur lecture, son échec. */
  presetValues: Partial<Record<QuickSourceKind, PresetValuesState>>;
  /** Relit les valeurs d'un référentiel dont la lecture a échoué. */
  onRetryPresetValues: (kind: QuickSourceKind) => void;
  path: string[];
  onPath: (path: string[]) => void;
  /** La cellule choisie : un identifiant de tuile, ou un article déversé (`@<article>`). */
  selected: string | null;
  onSelect: (key: string | null) => void;
  multi: Set<string> | null;
  onMulti: (m: Set<string> | null) => void;
  onLayout: (next: StudioLayout, toast?: string) => void;
  /** Ouvre la fenêtre d'ajout d'articles, pour ce niveau. */
  onPickArticles: (parentId: string | null, rail: boolean) => void;
  disabled: boolean;
}

export function QuickStudioComposer(p: ComposerProps) {
  const { layout, articles, sources, pours } = p;
  const path = effectivePath(layout, p.path);
  const level = path.length > 0 ? path[path.length - 1]! : null;
  const levelTile = level === null ? null : tileById(layout, level) ?? null;
  const inSource = levelTile !== null && isSourceKind(levelTile.kind);
  // L'habillage que la source du niveau donne à ses articles — `null` hors d'une source.
  const levelPour = levelTile && isSourceKind(levelTile.kind) ? levelTile.pour : null;
  const info = levelCells(layout, level, pours);
  const cells = info.keys;
  const rail = layout.settings.rail;
  // `pick` : la nature de source dont on choisit la valeur — le menu d'ajout devient la liste du référentiel.
  const [addAt, setAddAt] = useState<{ x: number; y: number; h: number; rail: boolean; presets?: true } | null>(null);
  /** La fenêtre « Choisir » d'une source qu'on ajoute — sa nature, et si la tuile ira au menu fixe. */
  const [choose, setChoose] = useState<{ kind: QuickSourceKind; rail: boolean } | null>(null);
  // ⚠️ DANS UNE TUILE DÉPLOYÉE, RIEN NE S'AJOUTE : elle n'est pas au document, une tuile posée dedans serait orpheline.
  const canAdd = !(levelTile && isPresetChildId(levelTile.id));
  // ⚠️ LE PLAFOND COMPTE LES TUILES POSÉES : celles qu'un préréglage déploie ne s'enregistrent pas.
  const posedCount = layout.tiles.filter((x) => !isPresetChildId(x.id)).length;
  const [findQ, setFindQ] = useState('');
  const [findOpen, setFindOpen] = useState(false);
  const [hit, setHit] = useState<string | null>(null);
  const [bulk, setBulk] = useState<'size' | 'move' | 'look' | 'color' | null>(null);
  // Le second clic sur la zone photo d'une tuile choisie : l'inspecteur cadre sa photo, ou en fait choisir une.
  const [photoAsk, setPhotoAsk] = useState<{ key: string; n: number } | null>(null);
  const hitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Le surlignage d'une tuile trouvée s'éteint seul ; quitter le composeur l'annule — il n'a plus rien à éteindre.
  useEffect(() => () => { if (hitTimer.current) clearTimeout(hitTimer.current); }, []);
  const gridRef = useRef<HTMLDivElement | null>(null);

  const nameOf = (tile: StudioTile): string => tileName(tile, articles, sources);
  const levelName = levelTile === null ? t('Écran d’accueil') : nameOf(levelTile);
  const hits = useMemo(() => findTiles(layout, findQ, articles, sources, pours), [layout, findQ, articles, sources, pours]);
  const multi = p.multi;

  // ⚠️ LE CONTENU D'UNE SOURCE SE LIT QUAND ON EN A BESOIN : son niveau ouvert, ses tuiles visibles (leur compte), le
  //    rail, et toutes les sources dès que la recherche s'ouvre — sans quoi « Où est cet article ? » ignorerait en
  //    silence ce que les sources fermées déversent.
  const wanted = useMemo(() => {
    const refs = new Set<string>();
    const want = (tile: StudioTile | undefined | null): void => { const r = tile ? sourceRefOf(tile) : null; if (r) refs.add(r); };
    want(levelTile);
    // ⚠️ Une tuile DÉPLOYÉE ne se lit qu'ouverte : un préréglage de marques en porte des centaines, et son compte vient
    //    du serveur (`sourceTileTotal`).
    const posed = (tile: StudioTile | undefined): void => { if (tile && !isPresetChildId(tile.id)) want(tile); };
    // ⚠️ SOUS CHAQUE TUILE AUSSI : sa pastille compte les articles de tous ses niveaux (`deepTileTotal`).
    const deep = (tile: StudioTile | undefined): void => {
      if (!tile) return;
      posed(tile);
      for (const id of descendantIds(layout, tile.id)) posed(tileById(layout, id));
    };
    for (const key of cells) if (!isPouredKey(key)) deep(tileById(layout, key));
    if (rail) for (const tile of railButtons(layout)) posed(tile);
    if (findOpen) for (const tile of layout.tiles) posed(tile);
    return [...refs].sort().join('|');
  }, [layout, levelTile, cells, rail, findOpen]);
  const { onNeedPours } = p;
  // ⚠️ `pours` EN DÉPENDANCE : un rechargement vide le contenu lu sans changer les sources voulues — sans elle, rien ne
  //    les redemanderait et le niveau resterait « Lecture du catalogue… ». Le conteneur ignore ce qu'il a déjà demandé.
  useEffect(() => { if (wanted) onNeedPours(wanted.split('|')); }, [wanted, onNeedPours, pours]);

  const drag = useTileDrag({
    keys: cells,
    axis: 'xy',
    disabled: p.disabled || multi !== null,
    ignoreSelector: '[data-qs-ctl]',
    // ⚠️ EN PLACE (maquette) : la tuile saisie reste à sa case, estompée, et le fantôme la rejoint au lâcher.
    inPlace: true,
    onReorder: (next) => p.onLayout(inSource ? reorderSource(layout, level!, next) : reorderLevel(layout, level, next)),
  });
  // LE MENU FIXE SE RANGE COMME LES TUILES, par sa poignée : l'ordre du menu est celui de l'accueil — donc de la caisse.
  // Ses cases vides (qu'un menu ne montre pas) restent en fin d'accueil (`reorderLevel`).
  const railTiles = rail ? railButtons(layout) : [];
  const railDrag = useTileDrag({
    keys: railTiles.map((x) => x.id),
    axis: 'y',
    disabled: p.disabled,
    inPlace: true,
    onReorder: (next) => p.onLayout(reorderLevel(layout, null, next)),
  });
  /**
   * RETIRER UN BOUTON DU MENU FIXE, c'est retirer la tuile de l'accueil — avec ce qu'elle contient. Immédiat, comme la
   * maquette : Ctrl+Z l'annule, et le message le dit avec ce qui part.
   */
  /** Déplace un bouton du menu d'un cran — le geste du clavier, sur le même ordre que la poignée. */
  const railMove = (id: string, delta: -1 | 1): void => {
    const ids = railTiles.map((x) => x.id);
    const i = ids.indexOf(id);
    const j = i + delta;
    if (p.disabled || i < 0 || j < 0 || j >= ids.length) return;
    ids.splice(i, 1);
    ids.splice(j, 0, id);
    p.onLayout(reorderLevel(layout, null, ids));
  };
  const railRemove = (tile: StudioTile): void => {
    const inside = descendantIds(layout, tile.id).size;
    p.onLayout(removeTiles(layout, [tile.id]), inside > 0
      ? t('« {nom} » retiré du menu avec ses {n} éléments — Ctrl+Z pour annuler', { nom: nameOf(tile), n: inside })
      : t('« {nom} » retiré du menu — Ctrl+Z pour annuler', { nom: nameOf(tile) }));
    if (p.selected === tile.id) p.onSelect(null);
    if (path.includes(tile.id)) p.onPath([]);
  };

  const add = (kind: AddKind, toRail: boolean): void => {
    if (kind === 'preset') { if (addAt) setAddAt({ ...addAt, presets: true }); return; }
    const parent = toRail ? null : level;
    // ⚠️ UNE SOURCE NAÎT CHOISIE : la fenêtre « Choisir » s'ouvre, et la tuile n'existe qu'au choix fait — sans source,
    //    elle ne déverserait rien, et le serveur refuserait le brouillon.
    if (isSourceKind(kind)) { setAddAt(null); if (posedCount < MAX_TILES) setChoose({ kind, rail: toRail }); return; }
    setAddAt(null);
    if (kind === 'article') { p.onPickArticles(parent, toRail); return; }
    if (posedCount >= MAX_TILES) return;
    const r = addTile(layout, parent, { kind, articleId: null, label: kind === 'folder' ? t('Nouveau dossier') : null, w: 1, h: 1 });
    p.onLayout(r.layout);
    p.onSelect(r.id);
    if (toRail && kind === 'folder') p.onPath([r.id]);
  };
  /** Pose un préréglage — nommé d'office (la caisse ne connaît pas le vocabulaire du magasin), renommable ensuite. */
  const addPreset = (kind: QuickPresetKind, toRail: boolean): void => {
    setAddAt(null);
    if (posedCount >= MAX_TILES) return;
    const r = addTile(layout, toRail ? null : level, { kind, articleId: null, label: presetLabel(kind), w: 1, h: 1 });
    p.onLayout(r.layout);
    p.onSelect(r.id);
    if (toRail) p.onPath([r.id]);
  };
  /** Pose la tuile d'une source qu'on vient de choisir — le nom de la tuile suit la source tant qu'on ne le change pas. */
  const addSource = (kind: QuickSourceKind, source: StudioSource, toRail: boolean): void => {
    setAddAt(null);
    const r = addTile(layout, toRail ? null : level, { kind, articleId: null, label: null, w: 1, h: 1, sourceId: source.id });
    p.onSource(source);
    p.onLayout(r.layout);
    p.onSelect(r.id);
    if (toRail) p.onPath([r.id]);
  };
  /**
   * RETIRER des cellules : une tuile part (un dossier plein demande confirmation — ses tuiles partent avec lui) ; un
   * article déversé est MASQUÉ, il reste au catalogue.
   */
  const remove = async (keys: string[]): Promise<void> => {
    const deployed = keys.filter(isPresetChildId);
    if (deployed.length > 0) {
      let next = layout;
      for (const key of deployed) { const c = presetChildOf(key)!; next = hidePresetValues(next, c.presetId, [c.valueId]); }
      p.onLayout(next, t('{n} valeurs retirées de ce préréglage — rétablissables depuis sa tuile', { n: deployed.length }));
      if (p.selected && keys.includes(p.selected)) p.onSelect(null);
      if (multi) p.onMulti(null);
      if (deployed.length === keys.length) return;
      keys = keys.filter((k) => !isPresetChildId(k));
    }
    const poured = keys.filter(isPouredKey).map(pouredArticle);
    const tileIds = keys.filter((k) => !isPouredKey(k));
    const inside = tileIds.reduce((n, id) => n + descendantIds(layout, id).size, 0);
    if (inside > 0 && !(await confirm({
      title: t('Retirer avec leur contenu ?'),
      message: t('Les dossiers retirés emportent {n} tuiles rangées dedans. Les articles restent au catalogue.', { n: inside }),
      confirmLabel: t('Retirer'),
    }))) return;
    let next = removeTiles(layout, tileIds);
    if (poured.length > 0 && inSource) next = hidePoured(next, level!, poured);
    // Dans une source, un article retiré — déversé ou retouché — y est masqué : il se rétablit, et le message le dit.
    const articlesOnly = inSource && tileIds.every((id) => tileById(layout, id)?.kind === 'article');
    p.onLayout(next, articlesOnly
      ? t('{n} articles retirés de cette tuile — rétablissables depuis son niveau', { n: keys.length })
      : t('{n} tuiles retirées — les articles restent au catalogue', { n: keys.length + inside }));
    if (p.selected && keys.includes(p.selected)) p.onSelect(null);
    if (path.some((id) => tileIds.includes(id))) p.onPath([]);
    if (multi) p.onMulti(null);
  };
  const openLevel = (id: string): void => { p.onPath(pathTo(layout, id)); p.onSelect(null); };
  const goFind = (h: FindHit): void => {
    p.onPath(pathTo(layout, h.level));
    p.onSelect(h.key);
    setFindOpen(false); setFindQ('');
    setHit(h.key);
    if (hitTimer.current) clearTimeout(hitTimer.current);
    hitTimer.current = setTimeout(() => setHit(null), HIT_MS);
  };
  /**
   * Ouvre le menu d'ajout sous le bouton, ou au-dessus s'il manque de place en bas (maquette `addPopAt`) : sa hauteur est
   * la place disponible, bornée entre 180 et 380 px, et il défile au-delà — jamais posé à moitié hors de l'écran.
   */
  const openAdd = (e: React.MouseEvent<HTMLElement>, toRail: boolean): void => {
    const r = e.currentTarget.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - 12;
    const above = r.top - 12;
    const up = below < 260 && above > below;
    const h = Math.round(Math.max(180, Math.min(380, up ? above : below)));
    setAddAt({ x: Math.max(8, Math.min(window.innerWidth - 292, r.left)), y: Math.round(up ? r.top - 6 - h : r.bottom + 6), h, rail: toRail });
  };

  /**
   * LE CLAVIER DE LA MAQUETTE : Suppr retire la sélection, les flèches déplacent la cellule choisie dans son niveau,
   * Échap referme ce qui est ouvert. Rien ne se déclenche depuis un champ de saisie, où ces touches appartiennent au champ.
   * ⚠️ CTRL+A (⌘A) COCHE TOUT LE NIVEAU en sélection multiple : la barre de la maquette n'a pas de « Tout sélectionner »,
   *    et le bouton qu'on y avait ajouté la faisait passer sur deux lignes — le geste reste, au clavier.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      // ⚠️ UNE TOUCHE DÉJÀ TRAITÉE PLUS BAS NE SE REJOUE PAS ICI : les flèches d'un choix (visuel, teinte) ou d'un bouton
      //    du menu fixe ne déplacent pas EN PLUS la tuile choisie.
      if (e.defaultPrevented) return;
      const target = e.target as HTMLElement | null;
      if (target && (/INPUT|TEXTAREA|SELECT/.test(target.tagName) || target.isContentEditable)) return;
      if (e.key === 'Escape') {
        setAddAt(null); setBulk(null); setFindOpen(false);
        if (multi) p.onMulti(null); else p.onSelect(null);
        return;
      }
      if (multi && (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        selectAll();
        return;
      }
      if (p.disabled) return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const keys = multi ? [...multi] : p.selected ? [p.selected] : [];
        if (keys.length === 0) return;
        e.preventDefault();
        void remove(keys);
        return;
      }
      if (/^Arrow/.test(e.key) && p.selected && !multi && !isPresetChildId(p.selected)) {
        // Haut et bas sautent d'une RANGÉE : le nombre de colonnes se lit sur la grille rendue, automatique compris.
        const cols = gridRef.current ? getComputedStyle(gridRef.current).gridTemplateColumns.split(' ').filter(Boolean).length : 1;
        const delta = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : e.key === 'ArrowUp' ? -cols : cols;
        e.preventDefault();
        p.onLayout(inSource ? nudgeInSource(layout, level!, cells, p.selected, delta) : nudgeTile(layout, p.selected, delta));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  /**
   * RETOUCHE DES CELLULES D'UN GESTE — un article déversé devient une tuile à sa place pour recevoir la retouche. Rend
   * les clés d'après le geste : celle d'un article déversé a changé, une sélection qui la garderait viserait une cellule
   * qui n'existe plus.
   * - `patchOf` : la retouche d'une cellule, ou `null` quand elle ne s'y applique pas (elle garde alors sa clé).
   */
  const retouch = (keys: Iterable<string>, patchOf: (kind: StudioTileKind) => TilePatch | null, toast: (n: number) => string): string[] => {
    let next = layout;
    let current = [...cells];
    const after: string[] = [];
    let changed = 0;
    for (const key of keys) {
      if (!isPouredKey(key)) {
        const tile = tileById(next, key);
        const patch = tile ? patchOf(tile.kind) : null;
        if (patch) { next = patchTile(next, key, patch); changed += 1; }
        after.push(key);
        continue;
      }
      const patch = patchOf('article');
      if (!inSource || !patch) { after.push(key); continue; }
      const r = materialize(next, level!, current, pouredArticle(key), patch);
      if (!r.id) continue;
      next = r.layout;
      current = current.map((k) => (k === key ? r.id : k));
      after.push(r.id);
      changed += 1;
    }
    p.onLayout(next, toast(changed));
    return after;
  };
  /** Change l'encombrement de cellules. */
  const resize = (keys: Iterable<string>, w: 1 | 2, h: 1 | 2, toast: string): string[] => retouch(keys, () => ({ w, h }), () => toast);
  /**
   * HABILLE TOUTE UNE SÉLECTION — un visuel, ou une taille de photo.
   * ⚠️ CE QUI NE S'APPLIQUE PAS À UNE CELLULE LA LAISSE TELLE QUELLE, et le compte le dit : une case vide ne s'habille pas,
   *    et une tuile déployée par un préréglage se règle depuis lui.
   */
  const dress = (keys: Iterable<string>, patch: TilePatch): string[] => retouch(keys, (kind) => {
    if (kind === 'empty') return null;
    return patch;
  }, (n) => t('{n} tuiles habillées', { n }));
  /** Toutes les cellules du niveau — ce que Ctrl+A coche en sélection multiple. */
  const selectAll = (): void => { p.onMulti(new Set(cells.filter((k) => !isPresetChildId(k)))); setBulk(null); };

  // ⚠️ LA GRILLE DE LA CAISSE, PAS CELLE DU NAVIGATEUR : chaque cellule dans sa version, à la hauteur de son contenu ou
  //    au côté de son carré, chaque rangée à celle de sa tuile la plus haute (`studioGrid`, charte). Le bouton « Ajouter »
  //    se pose sur la première case libre, sans rien régler.
  const gridWidth = useGridWidth(gridRef, 4 * QUICK_TILE.width + 3 * QUICK_TILE.gap);
  const shown = drag.order.flatMap((key) => {
    const d = drawnOf(key, layout, articles, levelPour);
    return d ? [{ key, d }] : [];
  });
  const grid = studioGrid(shown.map(({ d }) => d.cell), gridWidth, layout.settings.cols);
  // ⚠️ LA TÊTE DE L'ARBRE, RAIL ACTIF, C'EST L'ACCUEIL *OU* LE BOUTON DU RAIL OUVERT — jamais « un bouton ouvert »
  //    seulement : un accueil sans dossier ni source n'ouvre rien d'office, et l'interrupteur qui active le rail y
  //    disparaissait aussitôt, sans plus aucun moyen de le couper.
  const railAtTop = path.length <= (rail ? 1 : 0);
  const crumbs = rail ? path : [null, ...path];
  const hidden = levelTile && inSource ? hiddenCount(levelTile, pours) : null;

  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <main style={{ flex: '1 1 520px', minWidth: 0 }}>
        {p.context}
        <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'visible' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '11px 16px', borderBottom: '1px solid var(--ink-100)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', minWidth: 0 }}>
              {crumbs.map((id, i) => {
                const last = i === crumbs.length - 1;
                const crumb = id === null ? undefined : tileById(layout, id);
                const name = id === null ? t('Écran d’accueil') : crumb ? nameOf(crumb) : '';
                return (
                  <span key={id ?? 'root'} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <button
                      type="button" onClick={() => { p.onPath(id === null ? [] : pathTo(layout, id)); p.onSelect(null); }}
                      style={{ height: 26, padding: '0 9px', border: 'none', borderRadius: 'var(--r-xs)', background: last ? 'var(--ink-100)' : 'transparent', color: last ? 'var(--ink-900)' : 'var(--ink-500)', fontWeight: last ? 700 : 500, fontSize: 'var(--t-body)' }}
                    >{name}</button>
                    {!last && <Icon name="chevronR" size={12} stroke={2.4} color="var(--ink-300)" />}
                  </span>
                );
              })}
            </span>
            <span style={{ flex: 1 }} />
            {railAtTop && (
              <Toggle label={t('Menu fixe')} icon="sidebar" on={rail} disabled={p.disabled}
                hint={t('Les tuiles de l’accueil deviennent un rail vertical à gauche en caisse')}
                onChange={(on) => { p.onLayout({ ...layout, settings: { ...layout.settings, rail: on } }); p.onPath([]); p.onSelect(null); }} />
            )}
            <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-400)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              {t('{n} tuiles · niveau « {niveau} »', { n: cells.length, niveau: levelName })}
            </span>
            <span style={{ position: 'relative', flex: 1, minWidth: 190, maxWidth: 320, marginLeft: 'auto' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 7, height: 32, padding: '0 10px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)' }}>
                <Icon name="search" size={13} stroke={2.2} color="var(--ink-400)" style={{ flexShrink: 0 }} />
                <input
                  value={findQ} onChange={(e) => { setFindQ(e.target.value); setFindOpen(true); }} onFocus={() => setFindOpen(true)}
                  placeholder={t('Où est cet article ?')} aria-label={t('Où est cet article ?')}
                  style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontSize: 'var(--t-body)', color: 'var(--ink-900)', outline: 'none' }}
                />
              </span>
              {findOpen && findQ.trim().length >= 2 && (
                <>
                  <span onClick={() => setFindOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 598 }} />
                  <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 600, width: 360, maxWidth: 'min(360px, 90vw)', maxHeight: 320, overflowY: 'auto', background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', boxShadow: 'var(--sh-popover)', padding: 5 }}>
                    {hits.map((h) => (
                      <button key={`${h.level ?? ''}/${h.key}`} type="button" onClick={() => goFind(h)}
                        style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '7px 9px', border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent', textAlign: 'left' }}>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {h.name}
                            {h.code ? <span style={{ fontFamily: MONO, fontSize: 'var(--t-sub)', fontWeight: 500, color: 'var(--ink-400)' }}> {h.code}</span> : null}
                          </span>
                          <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {h.kind} · {h.level === null ? t('Écran d’accueil') : pathTo(layout, h.level).map((id) => { const x = tileById(layout, id); return x ? nameOf(x) : ''; }).join(' › ')}
                          </span>
                        </span>
                        <Icon name="chevronR" size={12} stroke={2.2} color="var(--ink-300)" style={{ flexShrink: 0 }} />
                      </button>
                    ))}
                    {hits.length === 0 && (
                      <div style={{ padding: '14px 10px', textAlign: 'center', fontSize: 'var(--t-dense)', color: 'var(--ink-400)' }}>{t('Aucune tuile ne correspond dans cette disposition.')}</div>
                    )}
                  </div>
                </>
              )}
            </span>
            {multi === null && (
              <button type="button" disabled={p.disabled} onClick={() => { p.onMulti(new Set()); p.onSelect(null); }} style={toolButton}>
                <Icon name="checkSquare" size={13} />{t('Sélectionner')}
              </button>
            )}
          </div>

          {inSource && levelTile && (info.pour !== 'ready' || (hidden ?? 0) > 0 || info.beyond > 0) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '9px 16px', borderBottom: '1px solid var(--ink-100)', background: 'var(--ink-50)', fontSize: 'var(--t-dense)', color: 'var(--ink-600)' }}>
              {info.pour === 'loading' && <span>{t('Lecture du catalogue…')}</span>}
              {info.pour === 'error' && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--rose-fg)' }}>
                  {t('Le contenu de cette tuile ne se lit pas : {motif}', { motif: info.message ?? '' })}
                  <button type="button" onClick={() => { const r = sourceRefOf(levelTile); if (r) p.onRetryPour(r); }} style={toolButton}>{t('Réessayer')}</button>
                </span>
              )}
              {(hidden ?? 0) > 0 && (
                <button type="button" disabled={p.disabled} title={t('Remettre dans ce niveau ce qui en a été retiré')}
                  onClick={() => p.onLayout(restoreHidden(layout, levelTile.id), t('Articles rétablis dans « {niveau} »', { niveau: levelName }))}
                  style={{ ...toolButton, border: '1px solid var(--amber-fg)', color: 'var(--amber-fg)' }}>
                  {t('{n} retirés — rétablir', { n: hidden ?? 0 })}
                </button>
              )}
              {info.beyond > 0 && (
                <span>{t('{n} articles de plus ne s’affichent pas en caisse — au-delà de {max}. Retirez-en, ou choisissez une valeur plus fine.', { n: info.beyond, max: QUICK_POUR_MAX })}</span>
              )}
            </div>
          )}

          {multi !== null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '9px 16px', borderBottom: '1px solid var(--ink-200)', background: 'var(--teal-100)' }}>
              <span style={{ flex: 1, minWidth: 160, fontSize: 'var(--t-body)', color: 'var(--teal-800)' }}>
                {/* ⚠️ UNE SEULE PHRASE, UNE SEULE CLÉ : coupée par la balise, chaque langue recevrait deux morceaux sans leur accord. */}
                <Trans k='Touchez les tuiles à cocher — <1>{n} sélectionnée</1>' vars={{ n: multi.size }} parts={[(x) => <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{x}</strong>]} />
              </span>
              <span style={{ position: 'relative', display: 'inline-flex', gap: 6 }}>
                <button type="button" disabled={multi.size === 0} onClick={() => setBulk(bulk === 'look' ? null : 'look')} className="oz-qs-sel-btn" style={selButton}>
                  {t('Affichage')}<Icon name="chevronD" size={11} stroke={2.4} />
                </button>
                <button type="button" disabled={multi.size === 0} onClick={() => setBulk(bulk === 'color' ? null : 'color')} className="oz-qs-sel-btn" style={selButton}>
                  {t('Couleur|teinte de la tuile')}<Icon name="chevronD" size={11} stroke={2.4} />
                </button>
                <button type="button" disabled={multi.size === 0} onClick={() => setBulk(bulk === 'size' ? null : 'size')} className="oz-qs-sel-btn" style={selButton}>
                  {t('Taille')}<Icon name="chevronD" size={11} stroke={2.4} />
                </button>
                {bulk === 'look' && (
                  <div style={{ ...popover(36, 'left', 220), padding: 6 }}>
                    {BULK_VISUALS.map((v) => (
                      <button key={v.key} type="button" className="oz-qs-add-kind" style={{ ...menuItem, fontWeight: 600 }}
                        onClick={() => { p.onMulti(new Set(dress(multi, { visual: v.key }))); setBulk(null); }}>
                        {v.label()}
                      </button>
                    ))}
                    <span style={{ display: 'block', margin: '6px 4px 4px', paddingTop: 6, borderTop: '1px solid var(--ink-100)', fontFamily: MONO, fontSize: 'var(--t-eyebrow)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>{t('Taille de la photo')}</span>
                    {BULK_SIZES.map((z) => (
                      <button key={z.key} type="button" className="oz-qs-add-kind" style={{ ...menuItem, padding: '6px 9px' }}
                        onClick={() => { p.onMulti(new Set(dress(multi, { photoSize: z.key }))); setBulk(null); }}>
                        <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-800)' }}>{z.label()}</span>
                        <span style={{ display: 'block', fontSize: 'var(--t-sub)', color: 'var(--ink-500)' }}>{z.hint()}</span>
                      </button>
                    ))}
                  </div>
                )}
                {bulk === 'color' && (
                  <div style={{ ...popover(36, 'left', 172), padding: 6 }}>
                    <span style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, padding: 2 }}>
                      {QUICK_TILE_COLORS.map((c) => (
                        <button key={c} type="button" aria-label={t(TILE_COLOR_NAME[c])} className="oz-qs-swatch"
                          onClick={() => { p.onMulti(new Set(dress(multi, { color: c }))); setBulk(null); }}
                          style={{ height: 30, borderRadius: 'var(--r-xs)', background: 'var(--paper)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                          <span style={{ width: 16, height: 16, borderRadius: 'var(--r-pill)', background: tileTone(c)!.dot }} />
                        </button>
                      ))}
                    </span>
                  </div>
                )}
                {bulk === 'size' && (
                  <div style={{ ...popover(36, 'left', 140), padding: 6 }}>
                    {SIZES.map((z) => (
                      <button key={`${z.w}x${z.h}`} type="button" className="oz-qs-add-kind" style={{ ...menuItem, fontFamily: MONO, fontWeight: 600 }}
                        onClick={() => {
                          p.onMulti(new Set(resize(multi, z.w, z.h, t('{n} tuiles modifiées', { n: multi.size }))));
                          setBulk(null);
                        }}>
                        {`${z.w} × ${z.h}`}
                      </button>
                    ))}
                  </div>
                )}
              </span>
              <span style={{ position: 'relative' }}>
                <button type="button" disabled={multi.size === 0} onClick={() => setBulk(bulk === 'move' ? null : 'move')} className="oz-qs-sel-btn" style={selButton}>
                  {t('Déplacer vers…')}<Icon name="chevronD" size={11} stroke={2.4} />
                </button>
                {bulk === 'move' && (
                  <div style={{ ...popover(36, 'right', 250), maxHeight: 280, overflowY: 'auto' }}>
                    <div style={popoverTitle}>{t('Ranger dans')}</div>
                    {folderTargets(layout, multi).filter((target) => target.id !== level).map((target) => (
                      <button key={target.id ?? 'root'} type="button" style={{ ...menuItem, paddingLeft: 8 + target.depth * 12 }}
                        onClick={() => {
                          const targetTile = target.id === null ? undefined : tileById(layout, target.id);
                          const name = targetTile ? nameOf(targetTile) : t('Écran d’accueil');
                          const keys = [...multi];
                          // Un article déversé rangé ailleurs y devient une tuile, et sort de sa source (rétablissable).
                          const poured = keys.filter(isPouredKey).map(pouredArticle);
                          let next = moveTiles(layout, keys.filter((k) => !isPouredKey(k)), target.id);
                          if (poured.length > 0 && inSource) next = hidePoured(addArticles(next, target.id, poured).layout, level!, poured);
                          p.onLayout(next, t('{n} tuiles déplacées vers « {cible} »', { n: multi.size, cible: name }));
                          p.onMulti(null); setBulk(null);
                        }}>
                        {target.id === null ? t('Écran d’accueil') : target.path.map((id) => { const x = tileById(layout, id); return x ? nameOf(x) : ''; }).join(' › ')}
                      </button>
                    ))}
                  </div>
                )}
              </span>
              <button type="button" disabled={multi.size === 0} onClick={() => { void remove([...multi]); }} className="oz-qs-sel-del"
                style={{ ...selButton, border: '1px solid var(--rose)', color: 'var(--rose-fg)' }}>
                {t('Retirer ({n})', { n: multi.size })}
              </button>
              <button type="button" onClick={() => { p.onMulti(null); setBulk(null); }}
                style={{ ...selButton, padding: '0 12px', border: 'none', background: 'var(--ink-900)', color: 'var(--paper)', fontWeight: 700 }}>
                {t('Terminer')}
              </button>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'stretch' }}>
            {rail && (
              <div style={railStyle(QUICK_RAIL.composerMaxHeight)}>
                {railDrag.order.map((id) => {
                  const tile = railTiles.find((x) => x.id === id);
                  if (!tile) return null;
                  const open = path[0] === tile.id;
                  return (
                    <RailButton key={tile.id} tile={tile} name={nameOf(tile)} open={open} active={open || p.selected === tile.id} screen={layout.settings}
                      article={tile.kind === 'article' ? articles[tile.articleId ?? ''] : undefined}
                      onClick={() => { if (opensLevel(tile.kind)) p.onPath([tile.id]); p.onSelect(tile.id); }}
                      edit={{ tile: railDrag.tile(tile.id), grip: railDrag.handle(tile.id), onRemove: () => railRemove(tile), onMove: (d) => railMove(tile.id, d), disabled: p.disabled }} />
                  );
                })}
                {/* Le cadre et les couleurs sont EN FEUILLE (`oz-quick-rail-add`) : en ligne, le survol ne gagnerait jamais. */}
                <button type="button" className="oz-quick-rail-add" disabled={p.disabled || posedCount >= MAX_TILES} onClick={(e) => openAdd(e, true)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%', height: 34, flexShrink: 0, borderRadius: 'var(--r-sm)', fontSize: QUICK_RAIL.font, fontWeight: 600 }}>
                  <Icon name="plus" size={12} />{t('Bouton')}
                </button>
              </div>
            )}
            <div ref={gridRef} style={{ flex: 1, minWidth: 0, padding: 16, ...grid.style }}>
              {shown.map(({ key, d: requested }, i) => {
                const { style: place, height, width, variant } = grid.place(i);
                // La version qui TIENT dans la tuile (`studioGrid`) : un visuel latéral s'efface quand le nom n'a plus la place.
                const d = variant === requested.cell.variant ? requested : { ...requested, cell: { ...requested.cell, variant } };
                const poured = isPouredKey(key);
                const tile = d.tile;
                const checked = multi?.has(key) ?? false;
                const hot = p.selected === key || checked;
                // La teinte EFFECTIVE : celle qu'un article hérite de sa source compte aussi pour sa sélection.
                const tone = tileTone(d.look.color);
                const deployed = isPresetChildId(key);
                const empty = tile?.kind === 'empty';
                const level = !!tile && opensLevel(tile.kind);
                const band = d.cell.variant === 'photo' || d.cell.variant === 'vignette' || d.cell.variant === 'vignetteReduced';
                // Où se posent le ✕, le › et la poignée sur CETTE tuile, et la place qu'ils prennent au contenu.
                const ctl = studioTileControls({ variant: d.cell.variant, height, level });
                const content = empty ? null : composerContent(d, layout, articles, sources, pours, p.presetValues, { height, width, controls: ctl });
                /** Ce que les boutons de la tuile nomment — sans lui, chaque tuile s'annonçait « Retirer la tuile ». */
                const tileName = content?.name ?? t('case vide');
                /** Choisir la tuile — ou la cocher en sélection multiple. Le clic et le clavier passent par ici. */
                const pick = (): void => {
                  // Une tuile déployée se retire ou se personnalise depuis l'inspecteur — pas en lot.
                  if (multi !== null && deployed) return;
                  if (multi !== null) {
                    const next = new Set(multi);
                    if (!next.delete(key)) next.add(key);
                    p.onMulti(next);
                  } else p.onSelect(key);
                };
                return (
                  // ⚠️ UN GROUPE FOCALISABLE, PAS UN BOUTON : la tuile porte ses boutons (retirer, ouvrir le niveau), qu'un
                  //    `role="button"` ne peut pas contenir. Entrée ou Espace la choisit ; choisie, les flèches la déplacent et
                  //    Suppr la retire (raccourcis du composeur). `aria-current` dit laquelle est choisie ou cochée.
                  <div
                    key={key} className="oz-qs-cell" data-qs-variant={d.cell.variant} {...drag.tile(key)} {...(multi === null && !deployed ? drag.handle(key) : {})}
                    role="group" aria-label={tileName} aria-current={hot || undefined} tabIndex={0}
                    onClick={(e) => {
                      // ⚠️ LE PREMIER CLIC CHOISIT TOUJOURS LA TUILE (maquette) : le cadrage, ou le choix d'un fichier,
                      //    ne s'ouvre qu'au clic suivant sur sa ZONE PHOTO — jamais par surprise. Une tuile déployée ou un
                      //    article déversé n'ont pas de photo propre : ils se choisissent seulement.
                      const onPhoto = (e.target as Element | null)?.closest?.('[data-qs-photo]');
                      if (multi === null && onPhoto && p.selected === key && !poured && !deployed) {
                        setPhotoAsk((prev) => ({ key, n: (prev?.n ?? 0) + 1 }));
                        return;
                      }
                      pick();
                    }}
                    onKeyDown={(e) => {
                      if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
                      e.preventDefault();
                      pick();
                    }}
                    style={{
                      ...(content ? studioTileFrame(content, level) : { boxSizing: 'border-box', minHeight: 0, display: 'flex', borderRadius: 'var(--r-sm)', paddingLeft: ctl.insetLeft }),
                      ...drag.tile(key).style, ...(multi === null && !deployed ? drag.handle(key).style : {}), ...place,
                      position: 'relative', cursor: deployed ? 'pointer' : multi === null ? 'grab' : 'pointer',
                      outline: hit === key ? '3px solid var(--teal-500)' : '3px solid transparent', outlineOffset: 3,
                      // ⚠️ LA SÉLECTION SE DIT PAR LE FILET, DANS LA TEINTE DE LA TUILE (maquette `skin`) : on règle la couleur d'une
                      //    tuile SÉLECTIONNÉE — un fond de sélection fixe la cacherait exactement pendant qu'on la choisit.
                      ...(empty
                        ? { border: `1.5px dashed ${hot ? 'var(--teal-400)' : 'var(--ink-200)'}`, background: 'transparent' }
                        // ⚠️ `--teal-bg` ET NON `--teal-100` : le nom est à l'encre du thème, qui bascule en sombre ; un fond pris à la
                        //    rampe claire ne bascule pas, et le nom d'une tuile sans teinte devenait illisible une fois choisie (Forme GK).
                        : hot ? { border: `1.5px solid ${tone?.dot ?? 'var(--teal-500)'}`, ...(!band && !tone ? { background: 'var(--teal-bg)' } : null) } : null),
                      boxShadow: hot ? '0 8px 22px -14px rgba(12,14,16,.34)' : 'none',
                    }}
                  >
                    {content
                      ? <StudioTileBody {...content} />
                      : <span style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: MONO, fontSize: 'var(--t-micro)', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ink-400)' }}>{t('case vide')}</span>}
                    {/* ⚠️ LA POIGNÉE DE LA MAQUETTE, TOUJOURS VISIBLE, en sélection comme ailleurs : elle ne fait rien d'autre que
                        DIRE qu'on déplace — toute la tuile se saisit. Toujours en haut à gauche (`studioTileControls`). Une tuile
                        déployée ne se déplace pas : elle n'en a pas. */}
                    {!deployed && (
                      <span aria-hidden data-qs-grip
                        style={{ position: 'absolute', ...ctl.grip, width: STUDIO_CONTROL.grip, height: STUDIO_CONTROL.grip, boxSizing: 'border-box', padding: 2, borderRadius: 'var(--r-xs)', background: webPaperVeil(QUICK_VEIL.code), color: 'var(--ink-400)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', zIndex: 2 }}>
                        <Icon name="drag" size={12} />
                      </span>
                    )}
                    {multi !== null ? (
                      <span data-qs-ctl style={{ position: 'absolute', top: 8, right: 8, zIndex: 2, width: 18, height: 18, boxSizing: 'border-box', borderRadius: 'var(--r-xs)', border: `1.5px solid ${checked ? 'var(--teal-500)' : 'var(--ink-300)'}`, background: checked ? 'var(--teal-500)' : 'var(--paper)', color: 'var(--paper)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                        {checked && <Icon name="check" size={11} stroke={3.2} />}
                      </span>
                    ) : (
                      <button data-qs-ctl type="button" aria-label={poured ? t('Retirer « {v} » de la tuile', { v: tileName }) : t('Retirer la tuile « {v} »', { v: tileName })} onClick={(e) => { e.stopPropagation(); void remove([key]); }}
                        className="oz-qs-tile-del"
                        style={{ position: 'absolute', top: STUDIO_CONTROL.edge, right: STUDIO_CONTROL.edge, zIndex: 2, width: STUDIO_CONTROL.button, height: STUDIO_CONTROL.button, padding: 0, border: 'none', borderRadius: 'var(--r-xs)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon name="x" size={12} stroke={2.6} />
                      </button>
                    )}
                    {/* ⚠️ LE › RESTE EN SÉLECTION (maquette) : ouvrir un niveau ne coche rien. */}
                    {ctl.open && (
                      <button data-qs-ctl data-qs-open type="button" aria-label={t('Ouvrir le niveau « {v} »', { v: tileName })} title={t('Ouvrir ce niveau')} onClick={(e) => { e.stopPropagation(); openLevel(key); }}
                        className="oz-qs-tile-open"
                        style={{ position: 'absolute', ...ctl.open, zIndex: 2, width: STUDIO_CONTROL.open, height: STUDIO_CONTROL.open, boxSizing: 'border-box', padding: 0, borderRadius: 'var(--r-xs)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon name="chevronR" size={12} stroke={2.2} />
                      </button>
                    )}
                  </div>
                );
              })}
              {canAdd && (
              // Le cadre et les couleurs sont EN FEUILLE (`oz-quick-tile-add`) : en ligne, le survol de la maquette ne gagnerait jamais.
              // ⚠️ LA HAUTEUR DE SA RANGÉE (`studioGrid`, `add`) : elle ne règle jamais une rangée, et la caisse n'a pas ce bouton.
              <button type="button" className="oz-quick-tile-add" disabled={p.disabled || posedCount >= MAX_TILES} onClick={(e) => openAdd(e, false)}
                style={{ ...grid.add, minHeight: 0, borderRadius: 'var(--r-sm)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 'var(--t-dense)', fontWeight: 600 }}>
                <Icon name="plus" size={20} stroke={2.2} />{t('Ajouter')}
              </button>
              )}
            </div>
          </div>

          {drag.ghost}
          {railDrag.ghost}
          <div style={{ display: 'flex', alignItems: 'center', gap: 13, flexWrap: 'wrap', padding: '11px 16px', borderTop: '1px solid var(--ink-200)', background: 'var(--ink-50)', fontSize: 'var(--t-note)', color: 'var(--ink-600)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Icon name="drag" size={13} color="var(--ink-300)" />{t('Glissez une tuile pour la replacer')}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 13, height: 13, borderRadius: 4, background: 'var(--teal-bg)', border: '1px solid var(--teal-400)' }} />
              {t('sélectionnée — réglages à droite')}
            </span>
            {posedCount >= MAX_TILES && <span style={{ color: 'var(--rose-fg)' }}>{t('Une disposition porte au plus {n} tuiles.', { n: MAX_TILES })}</span>}
          </div>
        </div>

        {choose && (
          <QuickStudioSourceChooser kind={choose.kind} onClose={() => setChoose(null)}
            onPick={(s) => { setChoose(null); addSource(choose.kind, s, choose.rail); }} />
        )}
        {addAt && (
          <>
            <span onClick={() => setAddAt(null)} style={{ position: 'fixed', inset: 0, zIndex: 598 }} />
            <div style={{ position: 'fixed', top: addAt.y, left: addAt.x, zIndex: 600, width: 284, maxHeight: addAt.h, overflowY: 'auto', background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', boxShadow: 'var(--sh-popover)', padding: 5 }}>
              {addAt.presets ? (
                <>
                  <button type="button" onClick={() => setAddAt({ ...addAt, presets: undefined })}
                    style={{ ...popoverTitle, display: 'flex', alignItems: 'center', gap: 5, width: '100%', border: 'none', background: 'transparent', textAlign: 'left' }}>
                    <Icon name="chevronL" size={11} />{t('Préréglage')}
                  </button>
                  {QUICK_PRESET_KINDS.map((k) => (
                    <button key={k} type="button" onClick={() => addPreset(k, addAt.rail)} style={menuItem}>
                      <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-900)' }}>{presetLabel(k)}</span>
                      <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 1 }}>{t('Une tuile par valeur, suivie en direct')}</span>
                    </button>
                  ))}
                  {DYNAMIC_PRESETS.map((label) => (
                    <button key={label()} type="button" disabled aria-disabled title={t('Pas encore disponible : ces listes demandent un calcul périodique que le serveur ne fait pas encore.')}
                      style={{ ...menuItem, opacity: 0.55, cursor: 'default' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-900)' }}>
                        {label()}<Badge tone="amber">{t('à venir')}</Badge>
                      </span>
                    </button>
                  ))}
                </>
              ) : (
              <>
              <div style={popoverTitle}>{addAt.rail ? t('Ajouter au menu fixe') : t('Ajouter dans ce niveau')}</div>
              {/* Une case vide n'a pas de sens dans le menu fixe : elle ne sépare rien dans une colonne de boutons. */}
              {ADD_KINDS.filter((k) => !(addAt.rail && k.kind === 'empty')).map((k) => (
                <button key={k.kind} type="button" className="oz-qs-add-kind"
                  onClick={() => add(k.kind, addAt.rail)}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: 9, width: '100%', padding: '8px 9px', border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent', textAlign: 'left', cursor: 'pointer' }}>
                  <span style={{ width: 26, height: 26, borderRadius: 'var(--r-xs)', background: k.tone.bg, color: k.tone.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon name={k.icon} size={14} />
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-900)' }}>
                      {k.kind === 'preset' ? t('Préréglage') : kindLabel(k.kind)}
                    </span>
                    <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 1, lineHeight: 1.35 }}>{k.hint()}</span>
                  </span>
                </button>
              ))}
              </>
              )}
            </div>
          </>
        )}
      </main>

      <QuickStudioInspector
        selectedKey={p.selected} layout={layout} level={level} cells={cells} articles={articles} sources={sources} pours={pours} photoAsk={photoAsk}
        disabled={p.disabled} onLayout={p.onLayout} onSelect={p.onSelect} onOpen={openLevel}
        onRemove={(key) => { void remove([key]); }} onSource={p.onSource}
        presetValues={p.presetValues} onRetryPresetValues={p.onRetryPresetValues}
      />
    </div>
  );
}

/** Une cellule du composeur : sa tuile (ou l'article déversé), l'article qu'elle porte, ce qu'elle montre et sa version. */
interface ComposerCell {
  tile: StudioTile | undefined;
  article: StudioArticle | undefined;
  /** Ce que la cellule montre, et sa teinte — héritée de la source du niveau pour un article (`studioFace`). */
  look: Pick<StudioTileContent, 'face' | 'framing'> & { color: StudioTile['color']; missingPhoto: QuickPhotoSize | null };
  cell: { w: number; h: number; variant: StudioTileContent['variant'] };
}

/**
 * CE QU'UNE CELLULE DESSINERA EN CAISSE — la règle du contrat (`studioFace`) et la version qui en découle
 * (`quickTileVariant`, charte). Dans le niveau d'une source, un article prend l'habillage qu'elle donne à ses articles
 * (`fromSource`), sauf ce que sa tuile pose. `null` : une tuile disparue.
 */
function drawnOf(key: string, layout: StudioLayout, articles: Record<string, StudioArticle>, fromSource: QuickTileLook | null): ComposerCell | null {
  if (isPouredKey(key)) {
    const article = articles[pouredArticle(key)];
    const look = studioFace(undefined, article, fromSource, layout.settings);
    return { tile: undefined, article, look, cell: { w: 1, h: 1, variant: quickTileVariant({ kind: 'article' }, shapeOf(look)) } };
  }
  const tile = tileById(layout, key);
  if (!tile) return null;
  const article = tile.kind === 'article' ? articles[tile.articleId ?? ''] : undefined;
  const look = studioFace(tile, article, fromSource, layout.settings);
  return { tile, article, look, cell: { w: tile.w, h: tile.h, variant: quickTileVariant(tile, shapeOf(look)) } };
}

/**
 * LA FORME QUE LE COMPOSEUR DESSINE — celle de la caisse, sauf pour une photo CHOISIE qui n'existe pas encore : sa place
 * est gardée (et dite, `photoToAdd`), là où la caisse montre le picto. Sans elle, « Photo » posé sur un dossier sans
 * photo ne changeait rien de visible, et le geste semblait ne pas prendre.
 * ⚠️ L'APERÇU NE LE FAIT PAS : il montre ce que la caisse montrera, le picto.
 */
function shapeOf(look: ComposerCell['look']): QuickTileFace | { kind: 'photo'; size: QuickPhotoSize } {
  return look.missingPhoto ? { kind: 'photo', size: look.missingPhoto } : look.face;
}

/** Les visuels qu'on pose d'un geste sur une sélection — ceux de l'inspecteur. */
const BULK_VISUALS: { key: QuickTileVisual; label: () => string }[] = [
  { key: 'none', label: () => t('Texte seul') },
  { key: 'icon', label: () => t('Petit picto') },
  { key: 'large_icon', label: () => t('Grand picto') },
  { key: 'photo', label: () => t('Photo') },
];

/** Les tailles de photo qu'on pose d'un geste sur une sélection, et ce que chacune dessine (maquette `blkPs`). */
const BULK_SIZES: { key: QuickPhotoSize; label: () => string; hint: () => string }[] = [
  { key: 'normal', label: () => t('Grande'), hint: () => t('Photo pleine tuile') },
  { key: 'reduced', label: () => t('Réduite'), hint: () => t('Bandeau fin au-dessus du nom') },
  { key: 'left', label: () => t('À gauche'), hint: () => t('Vignette à côté du texte') },
];

/** L'habillage que porte la tuile elle-même, champ par champ. */
function ownLook(tile: StudioTile): QuickTileLook {
  return { color: tile.color, visual: tile.visual, icon: tile.icon, photoSize: tile.photoSize };
}

/** Ce qu'un geste du bloc « Ses articles » change : les seuls champs d'un habillage d'articles (aucune photo). */
function pourPatch(patch: TilePatch): Partial<QuickTileLook> {
  const out: Partial<QuickTileLook> = {};
  if (patch.color !== undefined) out.color = patch.color;
  if (patch.visual !== undefined) out.visual = patch.visual;
  if (patch.icon !== undefined) out.icon = patch.icon;
  if (patch.photoSize !== undefined) out.photoSize = patch.photoSize;
  return out;
}

/**
 * CE QUE LA CELLULE MONTRE AU COMPOSEUR — la tuile telle que la caisse la dessinera (`StudioTileBody`), plus ce que seul
 * le composeur dit sur sa ligne : un article qui n'ira pas en caisse, les valeurs retirées d'une source ou d'un préréglage.
 */
function composerContent(d: ComposerCell, layout: StudioLayout, articles: Record<string, StudioArticle>, sources: Record<string, StudioSource>,
  pours: Record<string, PourState>, presetValues: Partial<Record<QuickSourceKind, PresetValuesState>>,
  at: { height: number; width: number; controls: StudioTileControls }): StudioTileContent {
  const { tile, article: a } = d;
  // Une photo à ajouter se dessine comme une absence de photo (les hachures), dans la forme de la photo.
  const face: QuickTileFace = d.look.missingPhoto ? { kind: 'default', size: d.look.missingPhoto } : d.look.face;
  const base = {
    face, framing: d.look.framing, variant: d.cell.variant, tone: tileTone(d.look.color), tall: (tile?.h ?? 1) > 1,
    height: at.height, width: at.width, controls: at.controls, photoToAdd: d.look.missingPhoto !== null,
  };
  if (!tile) {
    return { ...base, name: a?.name ?? t('Lecture…'), article: a ? articleContent(a) : { price: '—', brand: '', code: '', size: null } };
  }
  if (tile.kind === 'article') {
    const off = !a || !a.active;
    return {
      ...base, name: tileName(tile, articles, sources),
      article: a ? articleContent(a) : { price: '—', brand: '', code: '', size: null },
      note: off ? <Badge tone="rose">{t('Pas en caisse')}</Badge> : undefined,
    };
  }
  const source = isSourceKind(tile.kind);
  const preset = isPresetKind(tile.kind);
  const masked = source ? hiddenCount(tile, pours) : preset ? presetHiddenCount(tile, presetValues) : null;
  return {
    ...base, name: tileName(tile, articles, sources),
    // Les articles qu'ouvre la tuile, tous niveaux confondus — « … » tant qu'une source ou un préréglage se lit.
    count: { n: deepTileTotal(layout, tile, pours, articles, sources, presetValues) },
    note: (masked ?? 0) > 0 ? (
      <span title={preset
        ? t('{n} valeurs retirées de ce préréglage — rétablissables depuis sa tuile', { n: masked ?? 0 })
        : t('{n} articles retirés de cette tuile — rétablissables depuis son niveau', { n: masked ?? 0 })}><Badge tone="amber">−{masked}</Badge></span>
    ) : undefined,
  };
}

/**
 * L'INSPECTEUR — les réglages de la cellule choisie : son libellé et son encombrement, la source d'une famille, d'une
 * sous-famille ou d'une marque, et ce qu'on peut faire d'un article déversé.
 * ⚠️ LE LIBELLÉ D'UN ARTICLE NE VAUT QUE POUR LE BOUTON : la ligne du ticket garde le nom de l'article, c'est elle
 *    qui est scellée. Et son PRIX n'est pas réglable ici — il vient de la fiche article, seul chemin de prix.
 */
function QuickStudioInspector({ selectedKey, layout, level, cells, articles, sources, pours, photoAsk, disabled, onLayout, onSelect, onOpen, onRemove, onSource, presetValues, onRetryPresetValues }: {
  selectedKey: string | null; layout: StudioLayout; level: string | null; cells: string[]; photoAsk: { key: string; n: number } | null;
  articles: Record<string, StudioArticle>; sources: Record<string, StudioSource>; pours: Record<string, PourState>; disabled: boolean;
  onLayout: (next: StudioLayout, toast?: string) => void; onSelect: (key: string | null) => void; onOpen: (id: string) => void;
  onRemove: (key: string) => void; onSource: (source: StudioSource) => void;
  presetValues: Partial<Record<QuickSourceKind, PresetValuesState>>; onRetryPresetValues: (kind: QuickSourceKind) => void;
}) {
  const poured = selectedKey !== null && isPouredKey(selectedKey) && level !== null ? articles[pouredArticle(selectedKey)] ?? null : null;
  const pouredSelected = selectedKey !== null && isPouredKey(selectedKey);
  const deployedSelected = selectedKey !== null && isPresetChildId(selectedKey);
  const found = selectedKey !== null && !pouredSelected ? tileById(layout, selectedKey) ?? null : null;
  const tile = deployedSelected ? null : found;
  const kind: StudioTileKind | null = pouredSelected ? 'article' : tile?.kind ?? null;
  return (
    <aside style={{ flex: '0 0 296px', minWidth: 0 }}>
      <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
        <div style={{ padding: '13px 15px', borderBottom: '1px solid var(--ink-200)', fontSize: 'var(--t-control)', fontWeight: 700, color: 'var(--ink-900)' }}>
          {kind ? t('{nature} — réglages', { nature: kindLabel(kind) }) : t('Réglages')}
        </div>
        {!tile && !pouredSelected && !(deployedSelected && found) && (
          <div style={{ padding: '30px 18px', textAlign: 'center' }}>
            <div style={{ width: 44, height: 44, margin: '0 auto 11px', borderRadius: 'var(--r-sm)', background: 'var(--ink-100)', color: 'var(--ink-300)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="grid" size={22} stroke={1.9} />
            </div>
            <div style={{ fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Aucune tuile sélectionnée')}</div>
            <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 4, lineHeight: 1.5 }}>{t('Touchez une tuile du canvas pour la renommer, la colorer, changer son pictogramme ou son encombrement.')}</div>
          </div>
        )}
        {pouredSelected && level !== null && selectedKey !== null && (
          <PouredInspector
            articleId={pouredArticle(selectedKey)} article={poured} layout={layout} level={level} cells={cells} disabled={disabled}
            onLayout={onLayout} onSelect={onSelect} onRemove={() => onRemove(selectedKey)}
          />
        )}
        {deployedSelected && found && (
          <DeployedInspector
            tile={found} layout={layout} articles={articles} sources={sources} pours={pours} disabled={disabled}
            onLayout={onLayout} onSelect={onSelect} onOpen={onOpen} onRemove={() => onRemove(found.id)}
          />
        )}
        {tile && (
          <TileInspector
            key={tile.id} tile={tile} layout={layout} articles={articles} sources={sources} pours={pours} disabled={disabled}
            photoAsk={photoAsk?.key === tile.id ? photoAsk.n : 0}
            onLayout={onLayout} onOpen={onOpen} onRemove={() => onRemove(tile.id)} onSource={onSource}
            presetValues={presetValues} onRetryPresetValues={onRetryPresetValues}
          />
        )}
      </div>
    </aside>
  );
}

/** Les réglages d'une tuile POSÉE — un article, un dossier, une case vide ou une source. */
function TileInspector({ tile, layout, articles, sources, pours, disabled, photoAsk, onLayout, onOpen, onRemove, onSource, presetValues, onRetryPresetValues }: {
  tile: StudioTile; layout: StudioLayout; articles: Record<string, StudioArticle>; sources: Record<string, StudioSource>;
  pours: Record<string, PourState>; disabled: boolean; photoAsk: number; onLayout: (next: StudioLayout, toast?: string) => void;
  onOpen: (id: string) => void; onRemove: () => void; onSource: (source: StudioSource) => void;
  presetValues: Partial<Record<QuickSourceKind, PresetValuesState>>; onRetryPresetValues: (kind: QuickSourceKind) => void;
}) {
  const presetKind = isPresetKind(tile.kind) ? PRESET_SOURCE[tile.kind] : null;
  const values = presetKind ? presetValues[presetKind] : undefined;
  // Un article posé dans le niveau d'une source hérite de ce qu'elle donne à ses articles : l'écran montre l'effet.
  const parent = tile.kind === 'article' && tile.parentId ? tileById(layout, tile.parentId) : undefined;
  const fromSource = parent && isSourceKind(parent.kind) ? parent.pour : null;
  const retired = presetHiddenCount(tile, presetValues);
  const [srcOpen, setSrcOpen] = useState(false);
  const a = tile.kind === 'article' ? articles[tile.articleId ?? ''] : undefined;
  const sourceKind = isSourceKind(tile.kind) ? tile.kind : null;
  const ref = sourceRefOf(tile);
  const source = ref ? sources[ref] : undefined;
  const pour = ref ? pours[ref] : undefined;
  /** Le nom que la tuile prend sans libellé à elle : celui de l'article ou de la valeur du référentiel. */
  const catalogName = tile.kind === 'article' ? (a?.name ?? '') : (source?.label ?? '');
  /** Change ce que la tuile déverse — ses retraits et son rangement désignaient l'ancienne source, ils repartent de zéro. */
  const pickSource = (s: StudioSource): void => {
    setSrcOpen(false);
    if (s.id === tile.sourceId) return;
    const retouched = tile.hidden.length > 0 || tile.order.length > 0;
    onSource(s);
    onLayout(setSource(layout, tile.id, s.id), retouched
      ? t('La tuile déverse désormais « {nom} » — ses retraits et son rangement repartent de zéro', { nom: s.label })
      : t('La tuile déverse désormais « {nom} »', { nom: s.label }));
  };
  const note = tile.kind === 'empty'
    ? t('Une case vide réserve la place : elle aligne les touches suivantes et sépare deux blocs. En caisse, elle n’est pas cliquable.')
    : tile.kind === 'article'
      ? t('Le prix affiché vient de la fiche article. Le libellé ne change que le bouton : le ticket garde le nom de l’article.')
      : tile.kind === 'family'
        ? t('À l’ouverture, cette tuile déverse tous les articles de la famille — le catalogue reste la source, rien à maintenir ici.')
        : tile.kind === 'sub_family'
          ? t('À l’ouverture, cette tuile déverse tous les articles de la sous-famille — le catalogue reste la source, rien à maintenir ici.')
          : isSourceKind(tile.kind)
            ? t('À l’ouverture, cette tuile déverse tous les articles de la marque — le catalogue reste la source, rien à maintenir ici.')
        : presetKind
          ? t('À l’ouverture, une tuile par valeur du référentiel qui a des articles en vente — une valeur ajoutée demain y paraît d’elle-même. Les tuiles posées dans ce niveau passent devant.')
          : t('Un dossier peut contenir d’autres dossiers : la profondeur n’est pas limitée, mais trois niveaux suffisent en pratique.');
  return (
    <>
      <div style={{ padding: '14px 15px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {sourceKind && (
          <div>
            <span style={fieldCaption}>{sourceWord(sourceKind)}</span>
            <div style={{ position: 'relative' }}>
              <button type="button" disabled={disabled} aria-expanded={srcOpen} onClick={() => setSrcOpen(!srcOpen)}
                style={{ width: '100%', height: 36, padding: '0 11px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 'var(--t-lead)', fontWeight: 600, color: source ? 'var(--ink-900)' : 'var(--rose-fg)', textAlign: 'left' }}>
                <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{source?.label ?? t('Introuvable au référentiel')}</span>
                <Icon name="chevronD" size={12} stroke={2.4} color="var(--ink-400)" style={{ flexShrink: 0 }} />
              </button>
              {srcOpen && (
                <>
                  <span onClick={() => setSrcOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 598 }} />
                  <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 600, background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', boxShadow: 'var(--sh-popover)', overflow: 'hidden' }}>
                    <QuickStudioSourceList kind={sourceKind} current={tile.sourceId} onPick={pickSource} />
                  </div>
                </>
              )}
            </div>
            {!source && <span style={{ ...fieldHint, color: 'var(--rose-fg)' }}>{t('Cette valeur n’existe plus : la publication refusera cette tuile. Choisissez-en une autre.')}</span>}
            {pour?.status === 'error'
              ? <span style={{ ...fieldHint, color: 'var(--rose-fg)' }}>{t('Le contenu de cette tuile ne se lit pas : {motif}', { motif: pour.message })}</span>
              : <span style={fieldHint}>{t('Le contenu est lu en direct dans le catalogue — rien à maintenir ici.')}</span>}
            {source && !source.active && (
              <span style={{ ...fieldHint, color: 'var(--amber-fg)' }}>{t('Cette valeur est désactivée au référentiel : la tuile déverse encore ses articles vendables.')}</span>
            )}
          </div>
        )}
        {tile.kind !== 'empty' && (
          <label>
            <span style={{ ...fieldCaption, marginBottom: 5 }}>{t('Libellé affiché en caisse')}</span>
            <input
              key={tile.id} disabled={disabled}
              // ⚠️ LE CHAMP MONTRE LE NOM QUE LA CAISSE AFFICHE (maquette) : le sien, sinon celui du catalogue. Le ramener à ce
              //    nom, ou le vider, rend la main au catalogue — un renommage de l'article s'y verra.
              defaultValue={tile.label ?? catalogName}
              onChange={(e) => {
                const v = e.target.value.trim().replace(/\s+/g, ' ');
                // ⚠️ Un dossier — un préréglage aussi — se nomme : vidé, il garde son dernier nom plutôt que de devenir un bouton muet.
                if ((tile.kind === 'folder' || presetKind) && !v) return;
                onLayout(patchTile(layout, tile.id, { label: v && v !== catalogName ? v : null }));
              }}
              onFocus={(e) => e.target.select()}
              maxLength={60}
              style={{ width: '100%', height: 36, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-900)' }}
            />
          </label>
        )}
        {presetKind && (
          <div>
            <span style={fieldCaption}>{t('Valeurs déployées')}</span>
            {values?.status === 'error' ? (
              <span style={{ ...fieldHint, color: 'var(--rose-fg)', marginTop: 0 }}>
                {t('Le référentiel ne se lit pas : {motif}', { motif: values.message })}{' '}
                <button type="button" onClick={() => onRetryPresetValues(presetKind)} style={{ border: 'none', background: 'transparent', padding: 0, color: 'inherit', textDecoration: 'underline', fontWeight: 700 }}>{t('Réessayer')}</button>
              </span>
            ) : values?.status !== 'ready' ? (
              <span style={{ ...fieldHint, marginTop: 0 }}>{t('Lecture du référentiel…')}</span>
            ) : (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 'var(--t-dense)', color: 'var(--ink-700)' }}>
                {t('{n} valeurs en vente', { n: values.rows.length })}
                {(retired ?? 0) > 0 && (
                  <>
                    <Badge tone="amber">{t('{n} retirées', { n: retired ?? 0 })}</Badge>
                    <button type="button" disabled={disabled} onClick={() => onLayout(restorePresetValues(layout, tile.id), t('Valeurs rétablies'))} style={toolButton}>{t('Rétablir')}</button>
                  </>
                )}
              </span>
            )}
          </div>
        )}
        {tile.kind !== 'empty' && (
          <TileLookEditor
            // Ce que la tuile montre RÉELLEMENT : le sien, ou ce que l'écran, ses articles ou sa source lui donnent (`screenLook`).
            look={{ ...tile, ...screenLook(tile.kind, ownLook(tile), tile.kind === 'article' ? fromSource : null, layout.settings) }} kind={tile.kind}
            name={tileName(tile, articles, sources)} frame={{ w: tile.w, h: tile.h }} disabled={disabled} inherited={fromSource !== null}
            shown={shownPhoto(studioFace(tile, a, fromSource, layout.settings).face)} photoAsk={photoAsk}
            onChange={(patch, toast) => onLayout(patchTile(layout, tile.id, patch), toast)} />
        )}
        {(sourceKind || presetKind) && (
          <section aria-label={t('Ses articles')} style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 12, borderTop: '1px solid var(--ink-100)' }}>
            <div>
              <span style={{ ...fieldCaption, color: 'var(--ink-800)', fontSize: 'var(--t-dense)' }}>{t('Ses articles')}</span>
              <span style={{ ...fieldHint, marginTop: 0 }}>
                {presetKind
                  ? t('L’habillage des articles de chacune de ses tuiles — un article qui entre demain dans une famille prend la même forme.')
                  : t('L’habillage des articles qu’elle déverse — un article qui y entre demain prend la même forme. Un article retouché à part garde son réglage.')}
              </span>
            </div>
            <TileLookEditor look={{ ...tile.pour, photo: null }} kind="article" name={tileName(tile, articles, sources)} frame={{ w: 1, h: 1 }}
              disabled={disabled} forArticles
              onChange={(patch) => onLayout(patchTile(layout, tile.id, { pour: { ...tile.pour, ...pourPatch(patch) } }), t('Habillage de ses articles mis à jour'))} />
            {(tile.pour.color !== null || tile.pour.visual !== null || tile.pour.icon !== null || tile.pour.photoSize !== null) && (
              <button type="button" disabled={disabled} onClick={() => onLayout(patchTile(layout, tile.id, { pour: NO_POUR }), t('Ses articles ne reçoivent plus son habillage'))}
                style={{ ...toolButton, alignSelf: 'flex-start' }}>{t('Retirer l’habillage de ses articles')}</button>
            )}
          </section>
        )}
        <SizePicker w={tile.w} h={tile.h} disabled={disabled} onPick={(w, h) => onLayout(patchTile(layout, tile.id, { w, h }))} />
        {/* ⚠️ CE QUE LA CAISSE NE FERA PAS SE DIT ICI (§23.0) : la maquette n'a pas d'article retiré du catalogue. */}
        {tile.kind === 'article' && (!a || !a.active) && (
          <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-600)' }}>
            {a ? (
              <span><Badge tone="rose">{t('Pas en caisse')}</Badge> {a.archived
                ? t('Article archivé : il n’existe plus sur la caisse, qui ne dessinera pas cette tuile.')
                : t('Article désactivé : la caisse ne dessinera pas cette tuile.')}</span>
            ) : (
              <span style={{ color: 'var(--rose-fg)' }}>{t('Cet article n’existe plus au catalogue : la publication refusera cette tuile.')}</span>
            )}
          </div>
        )}
        <div style={{ padding: '11px 12px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', fontSize: 'var(--t-note)', color: 'var(--ink-600)', lineHeight: 1.5 }}>{note}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 15px', borderTop: '1px solid var(--ink-100)', background: 'var(--ink-50)' }}>
        {opensLevel(tile.kind) && (
          <button type="button" onClick={() => onOpen(tile.id)} className="oz-qs-insp-btn" style={inspectorButton}>{t('Ouvrir ce niveau')}</button>
        )}
        <span style={{ flex: 1 }} />
        <button type="button" disabled={disabled} onClick={onRemove} className="oz-qs-insp-del"
          style={{ ...inspectorButton, fontWeight: 700 }}>{t('Retirer')}</button>
      </div>
    </>
  );
}

/**
 * UNE TUILE DÉPLOYÉE PAR UN PRÉRÉGLAGE — elle suit le référentiel : on la retire (sa valeur est masquée, rétablissable
 * depuis le préréglage) ou on la PERSONNALISE (une vraie tuile de cette valeur prend sa place, et se règle comme toute
 * tuile). ⚠️ Aucun autre réglage ici : la tuile n'est pas au document, un réglage posé dessus ne serait pas gardé.
 */
function DeployedInspector({ tile, layout, articles, sources, pours, disabled, onLayout, onSelect, onOpen, onRemove }: {
  tile: StudioTile; layout: StudioLayout; articles: Record<string, StudioArticle>; sources: Record<string, StudioSource>;
  pours: Record<string, PourState>; disabled: boolean; onLayout: (next: StudioLayout, toast?: string) => void;
  onSelect: (key: string | null) => void; onOpen: (id: string) => void; onRemove: () => void;
}) {
  const count = sourceTileTotal(layout, tile, pours, articles, sources);
  const name = tileName(tile, articles, sources);
  const customize = (): void => {
    const r = customizePresetValue(layout, tile.id);
    if (!r) return;
    onLayout(r.layout, t('« {nom} » devient une tuile à part entière', { nom: name }));
    onSelect(r.id);
  };
  return (
    <>
      <div style={{ padding: '14px 15px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <span style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)' }}>{name}</span>
        <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-600)' }}>{count === null ? t('Lecture…') : t('{n} articles', { n: count })}</span>
        <div style={{ padding: '11px 12px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', fontSize: 'var(--t-note)', color: 'var(--ink-600)', lineHeight: 1.5 }}>
          {t('Cette tuile vient du préréglage : elle suit le référentiel. Personnalisez-la pour la renommer, l’habiller ou la ranger — une tuile à part entière prend sa place.')}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 15px', borderTop: '1px solid var(--ink-100)', background: 'var(--ink-50)' }}>
        <button type="button" onClick={() => onOpen(tile.id)} className="oz-qs-insp-btn" style={inspectorButton}>{t('Ouvrir ce niveau')}</button>
        <button type="button" disabled={disabled} onClick={customize} className="oz-qs-insp-btn" style={inspectorButton}>{t('Personnaliser')}</button>
        <span style={{ flex: 1 }} />
        <button type="button" disabled={disabled} onClick={onRemove} className="oz-qs-insp-del"
          style={{ ...inspectorButton, fontWeight: 700 }}>{t('Retirer')}</button>
      </div>
    </>
  );
}

/**
 * Les réglages d'un article DÉVERSÉ — le retoucher en fait une tuile à part entière, à sa place.
 * ⚠️ LE LIBELLÉ S'ENREGISTRE EN QUITTANT LE CHAMP, pas à la frappe : la première lettre ferait de l'article une tuile
 *    neuve, et le champ, rattaché à cette nouvelle tuile, perdrait la main au milieu du mot.
 */
function PouredInspector({ articleId, article, layout, level, cells, disabled, onLayout, onSelect, onRemove }: {
  articleId: string; article: StudioArticle | null; layout: StudioLayout; level: string; cells: string[]; disabled: boolean;
  onLayout: (next: StudioLayout, toast?: string) => void; onSelect: (key: string | null) => void; onRemove: () => void;
}) {
  // Ce que la source du niveau donne à ses articles : l'article déversé le porte, et l'écran le montre.
  const source = tileById(layout, level);
  const fromSource = source && isSourceKind(source.kind) ? source.pour : null;
  const realize = (patch: TilePatch): void => {
    const r = materialize(layout, level, cells, articleId, patch);
    if (!r.id) return;
    onLayout(r.layout, t('« {nom} » devient une tuile à part entière', { nom: patch.label ?? article?.name ?? '' }));
    onSelect(r.id);
  };
  return (
    <>
      <div style={{ padding: '14px 15px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <label>
          <span style={{ ...fieldCaption, marginBottom: 5 }}>{t('Libellé affiché en caisse')}</span>
          {/* ⚠️ LE NOM QUE LA CAISSE AFFICHE, DANS LE CHAMP (maquette) : le garder tel quel ne retouche rien. */}
          <input
            key={articleId} disabled={disabled} defaultValue={article?.name ?? ''} maxLength={60}
            onBlur={(e) => { const v = e.target.value.trim().replace(/\s+/g, ' '); if (v && v !== (article?.name ?? '')) realize({ label: v }); }}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            style={{ width: '100%', height: 36, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-900)' }}
          />
        </label>
        <TileLookEditor look={{ ...NO_LOOK, ...screenLook('article', null, fromSource, layout.settings) }} kind="article" name={article?.name ?? ''} frame={{ w: 1, h: 1 }}
          disabled={disabled} inherited={fromSource !== null} onChange={(patch) => realize(patch)} />
        <SizePicker w={1} h={1} disabled={disabled} onPick={(w, h) => { if (w !== 1 || h !== 1) realize({ w, h }); }} />
        <div style={{ padding: '11px 12px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', fontSize: 'var(--t-note)', color: 'var(--ink-600)', lineHeight: 1.5 }}>
          {t('Cet article vient de la tuile qui le déverse. Dès que vous le retouchez (libellé, visuel, couleur, encombrement), il devient une tuile à part entière — à la même place — et cesse de la suivre.')}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 15px', borderTop: '1px solid var(--ink-100)', background: 'var(--ink-50)' }}>
        <span style={{ flex: 1 }} />
        <button type="button" disabled={disabled} onClick={onRemove} className="oz-qs-insp-del"
          style={{ ...inspectorButton, fontWeight: 700 }}>{t('Retirer')}</button>
      </div>
    </>
  );
}

/** L'encombrement d'une tuile, en cases de la grille. */
function SizePicker({ w, h, disabled, onPick }: { w: 1 | 2; h: 1 | 2; disabled: boolean; onPick: (w: 1 | 2, h: 1 | 2) => void }) {
  return (
    <div>
      <span style={fieldCaption}>{t('Encombrement')}</span>
      <div style={{ display: 'flex', gap: 6 }}>
        {SIZES.map((z) => {
          const on = w === z.w && h === z.h;
          return (
            <button key={`${z.w}x${z.h}`} type="button" disabled={disabled} onClick={() => onPick(z.w, z.h)}
              style={{ flex: 1, height: 34, border: `1px solid ${on ? 'var(--teal-400)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-xs)', background: on ? 'var(--teal-100)' : 'var(--paper)', color: on ? 'var(--teal-800)' : 'var(--ink-700)', fontFamily: MONO, fontSize: 'var(--t-note)', fontWeight: 700 }}>
              {`${z.w} × ${z.h}`}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Un interrupteur de la barre du composeur — libellé, et la bascule dessinée par la charte. */
function Toggle({ label, icon, on, hint, disabled, onChange }: { label: string; icon?: IconName; on: boolean; hint: string; disabled: boolean; onChange: (on: boolean) => void }) {
  return (
    <button type="button" disabled={disabled} title={hint} aria-pressed={on} onClick={() => onChange(!on)}
      style={{ ...toolButton, gap: 8, padding: '0 10px 0 11px' }}>
      {icon && <span aria-hidden style={{ display: 'inline-flex', color: 'var(--ink-600)' }}><Icon name={icon} size={14} stroke={2} /></span>}
      <span style={{ fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink-800)' }}>{label}</span>
      <span aria-hidden style={{ width: 34, height: 20, borderRadius: 'var(--r-pill)', background: on ? 'var(--teal-500)' : 'var(--ink-200)', display: 'flex', alignItems: 'center', padding: 3, justifyContent: on ? 'flex-end' : 'flex-start', transition: 'background .13s' }}>
        <span style={{ width: 14, height: 14, borderRadius: 'var(--r-pill)', background: 'var(--paper)' }} />
      </span>
    </button>
  );
}

function Badge({ tone, children }: { tone: 'rose' | 'amber'; children: React.ReactNode }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', height: 18, padding: '0 7px', borderRadius: 'var(--r-pill)', fontSize: 'var(--t-micro)', fontWeight: 700, background: `var(--${tone}-bg)`, color: `var(--${tone}-fg)` }}>
      {children}
    </span>
  );
}

/** Un bouton du pied de l'inspecteur (maquette : 34 px, 12,5 px) — ses couleurs et son survol EN FEUILLE (`oz-qs-insp-*`). */
const inspectorButton: React.CSSProperties = {
  height: 34, padding: '0 13px', borderRadius: 'var(--r-ctrl)', fontSize: 'var(--t-body)', fontWeight: 600, whiteSpace: 'nowrap',
};
/** Un bouton de la barre de sélection (maquette : filet plus appuyé que celui de la barre d'outils). */
const selButton: React.CSSProperties = {
  height: 30, padding: '0 11px', borderRadius: 'var(--r-ctrl)', display: 'inline-flex', alignItems: 'center', gap: 6,
  fontSize: 'var(--t-dense)', fontWeight: 600, whiteSpace: 'nowrap',
};
const toolButton: React.CSSProperties = {
  height: 30, padding: '0 11px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)',
  color: 'var(--ink-800)', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-dense)', fontWeight: 600, whiteSpace: 'nowrap',
};
const menuItem: React.CSSProperties = {
  display: 'block', width: '100%', padding: '7px 9px', border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent',
  textAlign: 'left', fontSize: 'var(--t-body)', color: 'var(--ink-800)',
};
const popoverTitle: React.CSSProperties = {
  padding: '7px 9px 5px', fontFamily: MONO, fontSize: 'var(--t-eyebrow)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)',
};
const fieldCaption: React.CSSProperties = { display: 'block', fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink-500)', marginBottom: 6 };
const fieldHint: React.CSSProperties = { display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-400)', marginTop: 5, lineHeight: 1.45 };
const popover = (top: number, side: 'left' | 'right', width: number): React.CSSProperties => ({
  position: 'absolute', top, [side]: 0, zIndex: 40, width, background: 'var(--paper)', border: '1px solid var(--ink-200)',
  borderRadius: 'var(--r-sm)', boxShadow: 'var(--sh-popover)', padding: 5,
});
